<template>
  <div class="attachment-card">
    <div class="attachment-card-inner">
      <div v-if="cardDescription" class="attachment-card-description-wrapper">
        <div
          v-html="sanitizedCardDescription"
          @click="$emit('on-card-description-click')"
          class="attachment-card-description"
        />
      </div>

      <figure class="attachment-media-image-wrapper">
        <img
          v-if="mediaType === 'image'"
          class="attachment-media-image"
          :src="preSignedLink" :alt="title"
        />
      </figure>

      <div class="attachment-card-info">
        <div class="attachment-card-info-description">
          <v-icon size="24" color="primary">{{ type === 'FILE' ? 'mdi-file-outline' : 'mdi-link' }}</v-icon>

          <div class="attachment-title">
            <span class="attachment-title-inner" v-if="type === 'FILE'">{{ title }}</span>
            <a v-else :href="preSignedLink" class="attachment-link" target="_blank" rel="noopener">{{ title }}</a>
          </div>
        </div>

        <div class="attachment-card-info-actions">
          <v-btn
            icon
            size="small"
            variant="text"
            class="action-button"
            @click="handleOpenChange(true)"
            v-if="type === 'FILE'"
          >
            <v-icon size="20">mdi-eye-outline</v-icon>
          </v-btn>

          <a
            class="action-button action-link"
            :href="preSignedLink"
            target="_blank"
            rel="noopener"
            v-else
          >
            <v-icon size="20">mdi-open-in-new</v-icon>
          </a>

          <v-btn
            v-if="type === 'FILE'"
            icon
            download
            size="small"
            variant="text"
            class="action-button"
            @click="handleDownload"
          >
            <v-icon size="20">mdi-tray-arrow-down</v-icon>
          </v-btn>

          <v-btn
            icon
            size="small"
            variant="text"
            class="action-button"
            @click="deleteAttachment"
          >
            <v-icon size="20">mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <card-attachment-media-dialog
      v-if="type === 'FILE'"
      :open="mediaDialogOpen"
      @open-change="handleOpenChange"
      :title="title"
      :url="preSignedLink"
      :media-type="mediaType"
    />
  </div>
</template>

<script>
import store from '@/store'
import Storage from '@/storage'
import { sanitizeRichText } from '@/utils/sanitize'
import CardAttachmentMediaDialog from '@/components/CardAttachmentMediaDialog'

export default {
  components: {
    CardAttachmentMediaDialog
  },
  props: {
    attachmentId: String,
    type: String,
    title: String,
    date: String,
    url: String,
    mimeType: String,
    cardDescription: String
  },
  data () {
    return {
      mediaDialogOpen: false,
      preSignedLink: '',
      storage: null
    }
  },
  computed: {
    mediaType () {
      switch (this.mimeType) {
        case 'image/apng':
        case 'image/avif':
        case 'image/gif':
        case 'image/jpeg':
        case 'image/png':
        case 'image/webp':
          return 'image'

        case 'image/svg+xml':
          return 'svg'

        default:
          return 'unknown'
      }
    },
    sanitizedCardDescription () {
      return sanitizeRichText(this.cardDescription)
    }
  },
  methods: {
    deleteAttachment () {
      const workflowId = this.$route.params.id
      store.dispatch('workflow/deleteCardAttachment', {
        workflowId,
        id: this.attachmentId
      })
    },
    handleOpenChange (open) {
      this.assignPreSignedLink()

      this.mediaDialogOpen = open
    },
    assignPreSignedLink () {
      if (this.type === 'LINK') {
        this.preSignedLink = this.url
      } else {
        const key = decodeURIComponent(this.url.split('?X-Amz')[0].split('/').slice(3).join('/'))

        this.storage
          .getFile(key)
          .then((newLink) => {
            this.preSignedLink = newLink
          })
      }
    },
    handleDownload () {
      const key = decodeURIComponent(this.url.split('?X-Amz')[0].split('/').slice(3).join('/'))

      this.storage
        .getFile(key)
        .then((signedURL) => {
          const link = document.createElement('a')
          link.href = signedURL
          link.download = this.title
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
    }
  },
  mounted () {
    this.storage = new Storage()
    this.assignPreSignedLink()
  }
}
</script>

<style scoped>
.attachment-card {
  border-radius: 8px;
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 #00000026;

  .attachment-card-inner {
    overflow: hidden;
    border-radius: 8px;

    .attachment-card-description-wrapper {
      padding: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);

      .attachment-card-description {
        cursor: pointer;
        font-size: 12px;
        color: var(--light-blue);
        text-decoration: underline;
      }
    }

    .attachment-media-image-wrapper {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .attachment-media-image {
        width: 100%;
        max-height: 246px;
        object-fit: cover;
      }
    }

    .attachment-card-info {
      gap: 16px;
      width: 100%;
      padding: 8px;
      display: flex;
      justify-content: space-between;

      .attachment-card-info-description {
        gap: 8px;
        align-items: center;
        display: inline-flex;
        width: calc(100% - 120px);

        .attachment-title {
          color: var(--dark-4);
          font-size: 12px;
          align-items: center;
          display: inline-flex;
          letter-spacing: -0.42px;
          width: calc(100% - 32px);

          .attachment-title-inner {
            width: 100%;
            color: inherit;
            overflow: hidden;
            white-space: nowrap;
            display: inline-block;
            text-overflow: ellipsis;
          }

          .attachment-link {
            width: 100%;
            color: inherit;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }
      }

      .attachment-card-info-actions {
        gap: 8px;
        align-items: center;
        display: inline-flex;

        .action-button {
          width: 24px !important;
          height: 24px !important;
          color: #878F99 !important;
          transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;

          &:hover {
            color: var(--dark-4) !important;
          }
        }

        .action-link {
          color: inherit;
          align-items: center;
          display: inline-flex;
          justify-content: center;
          text-decoration: none;
          transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
      }
    }
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
}
</style>
